import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { cases } from "@/lib/endpoints";
import { AppHeader } from "@/components/AppHeader";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";
import type { CaseResponse } from "@/lib/api-types";

export function DashboardPage() {
  const { user } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["cases"],
    queryFn: () => cases.list(),
  });

  const firstName = user?.full_name?.split(" ")[0] ?? "";

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      <AppHeader />

      <main className="flex-1 max-w-3xl mx-auto w-full px-6 py-10">

        {/* Greeting */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-8 gap-3 sm:gap-4">
          <div>
            <h1 className="text-2xl font-bold font-display text-slate-900 tracking-tight">
              {firstName ? `Welcome back, ${firstName}` : "Your applications"}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Pick up where you left off, or start a new N-400 application.
            </p>
          </div>
          <Link
            to="/cases/new"
            className="self-start sm:shrink-0 inline-flex items-center gap-2 rounded-xl bg-slate-900 text-white px-5 py-2.5 text-sm font-semibold hover:bg-slate-800 hover:-translate-y-0.5 hover:shadow-md transition-all duration-150"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            New application
          </Link>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200/80 p-5 h-24 animate-pulse" />
            ))}
          </div>
        ) : !data || data.items.length === 0 ? (
          <EmptyState />
        ) : (
          <ul className="space-y-3">
            {data.items.map((c) => (
              <CaseCard key={c.id} c={c} />
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Case card
// ---------------------------------------------------------------------------

function CaseCard({ c }: { c: CaseResponse }) {
  const isPaid = !!c.paid_at;
  const isEligible = c.eligibility_status === "eligible";
  const isOOS = c.eligibility_status === "out_of_scope";

  // Three-step progress: eligibility → application → paid
  const step1Done = isEligible;
  const step3Done = isPaid;

  // Smart CTA
  const ctaHref = isPaid
    ? `/cases/${c.id}/wizard/review`
    : isEligible
      ? `/cases/${c.id}/wizard/checkout`
      : isOOS
        ? `/cases/${c.id}`
        : c.eligibility_status
          ? `/cases/${c.id}/wizard`
          : `/cases/${c.id}/gating`;

  const ctaLabel = isPaid
    ? "Generate →"
    : isEligible
      ? "Pay $710 →"
      : isOOS
        ? "View →"
        : c.eligibility_status
          ? "Continue →"
          : "Check eligibility →";

  const ctaStyle = isPaid
    ? "bg-emerald-600 hover:bg-emerald-700 text-white"
    : isEligible
      ? "bg-slate-900 hover:bg-slate-800 text-white"
      : "bg-white border border-slate-200 hover:bg-slate-50 text-slate-700";

  // Date
  const date = new Date(c.created_at);
  const dateStr = date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

  return (
    <li className="bg-white rounded-2xl border border-slate-200/80 shadow-card hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-150">
      <div className="p-5 flex items-center gap-4">

        {/* Icon */}
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-600 to-teal-500 flex items-center justify-center shrink-0 shadow-sm">
          <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-semibold text-slate-900 text-sm">Form N-400</span>
            <StatusPill status={c.status} />
            {c.eligibility_status && <EligibilityPill status={c.eligibility_status} />}
          </div>

          {/* 3-step progress dots */}
          <div className="flex items-center gap-1.5 mt-2.5">
            <StepDot done={step1Done} label="Eligibility" />
            <div className="h-px w-3 bg-slate-200 shrink-0" />
            <StepDot done={step1Done} label="Application" active={isEligible} />
            <div className="h-px w-3 bg-slate-200 shrink-0" />
            <StepDot done={step3Done} label="Payment" active={isEligible && !isPaid} />
          </div>
        </div>

        {/* Right side */}
        <div className="shrink-0 flex flex-col items-end gap-2">
          {/* Open workspace (chat-first view) */}
          <Link
            to={`/cases/${c.id}/workspace`}
            className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-all duration-150 hover:-translate-y-0.5 hover:shadow-sm"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round"
                d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-3 3-3-3z" />
            </svg>
            Open assistant
          </Link>
          {/* Secondary CTA — form wizard / payment / generate */}
          <Link
            to={ctaHref}
            className={cn(
              "inline-flex items-center rounded-xl px-4 py-2 text-xs font-semibold transition-all duration-150 hover:-translate-y-0.5 hover:shadow-sm",
              ctaStyle,
            )}
          >
            {ctaLabel}
          </Link>
          <span className="text-[11px] text-slate-400">{dateStr}</span>
        </div>
      </div>
    </li>
  );
}

function StepDot({ done, label, active }: { done: boolean; label: string; active?: boolean }) {
  return (
    <div className="flex items-center gap-1">
      <div className={cn(
        "w-4 h-4 rounded-full flex items-center justify-center transition-colors",
        done
          ? "bg-emerald-500"
          : active
            ? "bg-blue-600"
            : "bg-slate-200",
      )}>
        {done && (
          <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        )}
      </div>
      <span className={cn(
        "text-[10px] font-medium hidden xs:inline sm:inline",
        done ? "text-emerald-600" : active ? "text-blue-600" : "text-slate-400",
      )}>
        {label}
      </span>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const map: Record<string, string> = {
    draft: "bg-slate-100 text-slate-500",
    in_progress: "bg-blue-50 text-blue-700",
    submitted: "bg-emerald-50 text-emerald-700",
    closed: "bg-slate-100 text-slate-400",
  };
  return (
    <span className={cn("text-[10px] font-semibold px-2 py-0.5 rounded-full", map[status] ?? "bg-slate-100 text-slate-500")}>
      {status.replace("_", " ")}
    </span>
  );
}

function EligibilityPill({ status }: { status: string }) {
  const map: Record<string, string> = {
    eligible: "bg-emerald-50 text-emerald-700",
    out_of_scope: "bg-slate-100 text-slate-400",
    manual_review: "bg-amber-50 text-amber-700",
  };
  const labels: Record<string, string> = {
    eligible: "✓ Eligible",
    out_of_scope: "Out of scope",
    manual_review: "⚠ Review needed",
  };
  return (
    <span className={cn("text-[10px] font-semibold px-2 py-0.5 rounded-full", map[status] ?? "bg-slate-100 text-slate-400")}>
      {labels[status] ?? status}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

function EmptyState() {
  return (
    <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-12 text-center shadow-sm">
      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-600 to-teal-500 flex items-center justify-center mx-auto mb-5 shadow-sm">
        <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      </div>
      <h3 className="text-lg font-bold font-display text-slate-900">No applications yet</h3>
      <p className="text-sm text-slate-500 mt-2 max-w-sm mx-auto leading-relaxed">
        Start a new N-400 application. We'll check your eligibility, walk you through every
        section, and generate a USCIS-ready PDF when you're done.
      </p>

      {/* Steps preview */}
      <div className="flex items-center justify-center gap-2 mt-6 mb-6">
        {["Eligibility check", "8-step wizard", "PDF packet"].map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            {i > 0 && <div className="h-px w-5 bg-slate-200" />}
            <span className="text-[11px] font-medium text-slate-400 bg-slate-50 border border-slate-200 px-2.5 py-1 rounded-full">{s}</span>
          </div>
        ))}
      </div>

      <Link
        to="/cases/new"
        className="inline-flex items-center gap-2 rounded-xl bg-slate-900 text-white px-6 py-2.5 text-sm font-semibold hover:bg-slate-800 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200"
      >
        Start your N-400 →
      </Link>
    </div>
  );
}
